import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { PaperPlaneRight, CheckCircle, SpinnerGap } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SERVICES, VEHICLE_TYPES, buildWhatsAppLink } from "@/data/services";
import { createRequest } from "@/lib/rescueApi";

const serviceLabel = (id) => SERVICES.find((s) => s.id === id)?.label || id;

const EMPTY = {
  name: "",
  phone: "",
  vehicle_type: "",
  service_type: "",
  location: "",
  notes: "",
};

const fieldClass =
  "bg-transparent border-white/20 text-white placeholder:text-white/35 focus-visible:border-rescue-primary focus-visible:ring-rescue-primary/40 h-12 rounded-sm";

export const RequestForm = ({ prefill }) => {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (prefill && prefill.nonce) {
      setForm((f) => ({
        ...f,
        service_type: prefill.service_type || f.service_type,
        notes: prefill.notes || f.notes,
      }));
    }
  }, [prefill]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.vehicle_type || !form.service_type || !form.location) {
      toast.error("Please fill in all required fields.");
      return;
    }
    if (!/^[0-9+\-\s]{7,15}$/.test(form.phone)) {
      toast.error("Please enter a valid phone number.");
      return;
    }
    // Open a blank tab within the user gesture so the WhatsApp notification
    // is not blocked by popup blockers after the async request.
    const waTab = window.open("", "_blank");
    setLoading(true);
    try {
      await createRequest(form);
      const message =
        `New RapidRescue enquiry\n\n` +
        `Name: ${form.name}\n` +
        `Phone: ${form.phone}\n` +
        `Vehicle: ${form.vehicle_type}\n` +
        `Service: ${serviceLabel(form.service_type)}\n` +
        `Location: ${form.location}\n` +
        `Notes: ${form.notes || "-"}`;
      const waLink = buildWhatsAppLink(message);
      if (waTab) {
        waTab.location.href = waLink;
      } else {
        window.open(waLink, "_blank");
      }
      setDone(true);
      toast.success("Help is on the way! Confirm your enquiry on WhatsApp.");
      setForm(EMPTY);
      setTimeout(() => setDone(false), 6000);
    } catch (err) {
      if (waTab) waTab.close();
      toast.error("Something went wrong. Please call +91 98926 48175.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="request" className="relative bg-rescue-bg py-24 sm:py-32">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
        <div className="lg:col-span-5">
          <span className="text-rescue-primary font-semibold uppercase tracking-[0.3em] text-xs">
            Command center
          </span>
          <h2 className="font-display uppercase tracking-tighter text-white text-6xl sm:text-7xl leading-[0.85] mt-4">
            Send your<br />rescue request.
          </h2>
          <p className="text-white/60 text-base leading-relaxed mt-6 max-w-md">
            Fill the tactical form — no account needed. The moment you hit dispatch, our control room
            routes the nearest crew to your location.
          </p>
          <div className="mt-10 flex items-center gap-4 border border-white/10 bg-rescue-surface p-5">
            <span className="grid place-items-center h-12 w-12 bg-rescue-primary/15 text-rescue-primary rounded-sm">
              <PaperPlaneRight weight="fill" size={22} />
            </span>
            <div>
              <div className="font-display uppercase text-2xl text-white leading-none">Live 24 × 7</div>
              <div className="text-white/55 text-sm mt-1">Average response under 18 minutes.</div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7">
          <motion.form
            data-testid="request-form"
            onSubmit={submit}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6 }}
            className="bg-rescue-surface border border-white/10 p-6 sm:p-8 grid grid-cols-1 sm:grid-cols-2 gap-5"
          >
            <div className="space-y-2">
              <Label htmlFor="name" className="text-white/70 text-xs uppercase tracking-widest">
                Full name *
              </Label>
              <Input
                id="name"
                data-testid="form-name"
                value={form.name}
                onChange={(e) => update("name", e.target.value)}
                placeholder="Your name"
                className={fieldClass}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone" className="text-white/70 text-xs uppercase tracking-widest">
                Phone *
              </Label>
              <Input
                id="phone"
                data-testid="form-phone"
                value={form.phone}
                onChange={(e) => update("phone", e.target.value)}
                placeholder="+91 98765 43210"
                className={fieldClass}
              />
            </div>

            <div className="space-y-2">
              <Label className="text-white/70 text-xs uppercase tracking-widest">Vehicle type *</Label>
              <Select value={form.vehicle_type} onValueChange={(v) => update("vehicle_type", v)}>
                <SelectTrigger data-testid="form-vehicle" className={fieldClass}>
                  <SelectValue placeholder="Select vehicle" />
                </SelectTrigger>
                <SelectContent className="bg-rescue-surface border-white/15 text-white">
                  {VEHICLE_TYPES.map((v) => (
                    <SelectItem key={v} value={v} data-testid={`vehicle-opt-${v}`}>
                      {v}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-white/70 text-xs uppercase tracking-widest">Service needed *</Label>
              <Select value={form.service_type} onValueChange={(v) => update("service_type", v)}>
                <SelectTrigger data-testid="form-service" className={fieldClass}>
                  <SelectValue placeholder="Select service" />
                </SelectTrigger>
                <SelectContent className="bg-rescue-surface border-white/15 text-white">
                  {SERVICES.map((s) => (
                    <SelectItem key={s.id} value={s.id} data-testid={`service-opt-${s.id}`}>
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="location" className="text-white/70 text-xs uppercase tracking-widest">
                Location *
              </Label>
              <Input
                id="location"
                data-testid="form-location"
                value={form.location}
                onChange={(e) => update("location", e.target.value)}
                placeholder="Landmark, area, city or a maps pin link"
                className={fieldClass}
              />
            </div>

            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="notes" className="text-white/70 text-xs uppercase tracking-widest">
                Notes (optional)
              </Label>
              <Textarea
                id="notes"
                data-testid="form-notes"
                value={form.notes}
                onChange={(e) => update("notes", e.target.value)}
                placeholder="Describe the problem, vehicle make/model, anything useful…"
                className="bg-transparent border-white/20 text-white placeholder:text-white/35 focus-visible:border-rescue-primary focus-visible:ring-rescue-primary/40 rounded-sm min-h-[96px]"
              />
            </div>

            <button
              type="submit"
              data-testid="request-submit-btn"
              disabled={loading}
              className="sm:col-span-2 mt-1 inline-flex items-center justify-center gap-3 bg-rescue-primary hover:brightness-110 active:scale-[0.98] disabled:opacity-60 transition-all text-white text-base font-semibold py-4 rounded-sm uppercase tracking-wide"
            >
              {loading ? (
                <>
                  <SpinnerGap className="animate-spin" size={20} /> Dispatching…
                </>
              ) : done ? (
                <>
                  <CheckCircle weight="fill" size={20} /> Request sent
                </>
              ) : (
                <>
                  Dispatch Help Now <PaperPlaneRight weight="fill" size={18} />
                </>
              )}
            </button>
          </motion.form>
        </div>
      </div>
    </section>
  );
};
